import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { T } from "@repo/transifex";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import {
  useAppDispatch,
  useDeviceData,
  usePrinterError
} from "~/providers/app";
import { authActions, useAuth } from "~/providers/store/auth";
import { useCurrentSession } from "~/api/sessions/hooks/use-session";
import { useInitFeatureFlagsContext } from "~/routes/__app/use-feature-flags-context";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";
import { logger } from "~/services/logger";
import { getErrorMessage } from "@repo/system";
import { ScreenLoader } from "~/components/screen/screen-loader";
import {
  ScreenError,
  ScreenErrorText,
  ScreenErrorTitle
} from "~/components/screen/screen-error";
import { ScreenIssue } from "~/components/screen/screen-issue";
import { NetworkStatus } from "~/components/network-status/network-status";

import { InvalidSessionScreen } from "./invalid-session-screen";
import {
  describeTillGate,
  resolveTillGate,
  tillGateLogProperties,
  type TillGate
} from "./resolve-till-gate";
import { NetworkIssueScreen } from "./network-issue-screen";

const PRINTER_ERROR_ANIMATION_DURATION = 15000;

const SAFE_PATHS_FOR_INVALID_SESSION = [
  "home",
  "support",
  "settings",
  "ticket-validation",
  "collect"
];

export const AppLayout = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { deviceId, tillId } = useDeviceData();
  const { pathname } = useLocation();

  const printerError = usePrinterError();

  const {
    data: till,
    status: tillStatus,
    fetchStatus: tillFetchStatus,
    error: tillError,
    failureReason: tillFailureReason,
    refetch: refetchTill
  } = useCurrentTill();
  const { data: session, status: sessionStatus } = useCurrentSession();

  const auth = useAuth();

  const { featureFlagsContextLoading } = useInitFeatureFlagsContext({
    tillId: auth.tillId ?? tillId
  });

  const gateInput = {
    authTillId: auth.tillId,
    isKanplaAdmin: auth.isKanplaAdmin,
    deviceId,
    till,
    status: tillStatus,
    fetchStatus: tillFetchStatus,
    error: tillError,
    failureReason: tillFailureReason,
    isOnline: auth.isOnline
  };

  const gate = resolveTillGate(gateInput);

  useTillGateLogging(gate, gateInput);

  if (gate.screen === "loading") {
    return (
      <ScreenLoader>
        <ScreenLoader.Title>
          <T _str="Connecting device" />
        </ScreenLoader.Title>

        <ScreenLoader.Text>
          <T _str="Please wait" />
        </ScreenLoader.Text>
      </ScreenLoader>
    );
  }

  if (printerError) {
    return (
      <ScreenError
        onClose={() => {
          dispatch({ type: "CLEAR_PRINTER_ERROR" });
        }}
        animationDuration={PRINTER_ERROR_ANIMATION_DURATION}
      >
        <ScreenErrorTitle>
          <T _str="Printer Error" />
        </ScreenErrorTitle>

        <ScreenErrorText>{printerError}</ScreenErrorText>
      </ScreenError>
    );
  }

  if (gate.screen === "logged-out") {
    return (
      <ScreenIssue>
        <ScreenIssue.Title>
          <T _str="You've been logged out" />
        </ScreenIssue.Title>

        <ScreenIssue.Button
          onClick={() => {
            authActions.logout();
            queryClient.clear();
          }}
        >
          <T _str="Log in" />
        </ScreenIssue.Button>
      </ScreenIssue>
    );
  }

  if (gate.screen === "offline") {
    return <NetworkIssueScreen />;
  }

  if (gate.screen === "load-error") {
    return (
      <ScreenIssue>
        <ScreenIssue.Title>
          <T _str="Error loading till" />
        </ScreenIssue.Title>

        <ScreenIssue.Text>{getErrorMessage(tillError)}</ScreenIssue.Text>

        <ScreenIssue.Button onClick={() => void refetchTill()}>
          <T _str="Retry" />
        </ScreenIssue.Button>
      </ScreenIssue>
    );
  }

  if (gate.screen === "license-missing") {
    return (
      <ScreenIssue>
        <ScreenIssue.Title>
          <T _str="Device doesn't have a license" />
        </ScreenIssue.Title>

        <ScreenIssue.Text>
          <T _str="Register this device to continue" />
        </ScreenIssue.Text>

        <ScreenIssue.Button
          onClick={() => {
            logger.warn("License registration started", {
              till_id: till?.id,
              till_device_id: till?.deviceId,
              device_id: deviceId
            });
            navigate("/license");
          }}
        >
          <T _str="Register Device" />
        </ScreenIssue.Button>
      </ScreenIssue>
    );
  }

  if (auth.status === "till") {
    return (
      <ScreenIssue>
        <ScreenIssue.Title>
          <T _str="You need a profile" />
        </ScreenIssue.Title>

        <ScreenIssue.Text>
          <T _str="Please select a profile to continue" />
        </ScreenIssue.Text>

        <ScreenIssue.Button
          onClick={() => {
            navigate("/profiles");
          }}
        >
          <T _str="Choose a profile" />
        </ScreenIssue.Button>
      </ScreenIssue>
    );
  }

  if (!auth.isOnline && till?.type === "self-service") {
    return <Navigate replace to="/offline" />;
  }

  if (till?.sessionId && sessionStatus === "pending") {
    return (
      <ScreenLoader>
        <ScreenLoader.Title>
          <T _str="Loading Till Session" />
        </ScreenLoader.Title>

        <ScreenLoader.Text>
          <T _str="Please wait" />
        </ScreenLoader.Text>

        <NetworkStatus />
      </ScreenLoader>
    );
  }

  if (featureFlagsContextLoading) {
    return (
      <ScreenLoader>
        <ScreenLoader.Title>
          <T _str="Loading feature configuration" />
        </ScreenLoader.Title>

        <ScreenLoader.Text>
          <T _str="Please wait" />
        </ScreenLoader.Text>
      </ScreenLoader>
    );
  }

  if (
    (!till?.sessionId || !session?.isValid) &&
    !SAFE_PATHS_FOR_INVALID_SESSION.some((path) => pathname.includes(path))
  ) {
    if (hasAttendedSellConfig(till)) {
      return <Navigate to="/home" />;
    }

    return <InvalidSessionScreen />;
  }

  return <Outlet />;
};

/**
 * Logs once per gate transition. The previous instrumentation only fired when
 * staff tapped "Register Device", so a gate that healed on its own left no
 * trace at all.
 */
function useTillGateLogging(
  gate: TillGate,
  input: Parameters<typeof resolveTillGate>[0]
) {
  const lastLogged = useRef<string | null>(null);

  const key =
    gate.screen === "license-missing"
      ? `${gate.screen}:${gate.reason}`
      : gate.screen;

  useEffect(() => {
    if (lastLogged.current === key) {
      return;
    }
    lastLogged.current = key;

    const entry = describeTillGate(gate);
    if (entry === null) {
      return;
    }

    const properties = tillGateLogProperties(input);
    if (entry.level === "error") {
      // A thrown ts-rest response carries the raw headers, which trips the
      // Sentry scrubber and comes back "[Filtered]" - wrap it so only the
      // message ships; `failure_*` in the properties carry the details.
      const requestError = input.error ?? input.failureReason;
      const error =
        requestError instanceof Error
          ? requestError
          : new Error(
              `Till request failed (status ${String(
                properties.failure_status ?? "unknown"
              )})`
            );
      logger.error(error, entry.message, properties);
    } else {
      logger.warn(entry.message, properties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one log per transition, keyed on the gate
  }, [key]);
}
